import React, { useCallback, useRef, useState } from 'react';
import NiceModal, { antdModalV5, useModal } from '@ebay/nice-modal-react';
import { Modal } from 'antd';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import OrbitThemeScope, { useOrbitThemeVariant } from '../OrbitThemeScope';
import { OrbitChartModel } from '../chartAdapters';
import { downloadOrbitChartImage } from '../chartExport';
import { TOrbit } from '../i18n/TModules';
import { orbitColorsFor, orbitTokens, orbitType } from '../styles';
import OrbitChart from './OrbitChart';
import OrbitChartActions from './OrbitChartActions';

export interface IOrbitChartExpandModalProps {
  model: OrbitChartModel;
}

const MODAL_WIDTH = 720;

// Positioning context for the download control, which has to sit OUTSIDE `Surface`: `Surface` is the
// node the export rasterises, and a control drawn inside it would be painted into the file.
const Frame = styled.div`
  position: relative;
`;

// The chart's own row fills are dark, but the donut ring and the line chart both leave the space
// around them to the host; the panel surface is what they are drawn against inside the panel.
const Surface = styled.div`
  padding: 16px;
  background: ${orbitTokens.surface};
  border-radius: 14px;
  color: ${orbitTokens.text};
  font-family: ${orbitTokens.fontFamily};
`;

// The plotted metric's name, drawn inside the capture box so the saved image says what it measures.
const Caption = styled.h4`
  margin: 0 0 12px;
  color: ${orbitTokens.text};
  ${orbitType.containerTitle}
  font-weight: 700;
`;

/**
 * The expand control's destination: the same chart at 720 rather than the 449 content column.
 * `OrbitChart` measures its own root, so the model is re-rendered at the wider size rather than
 * scaled.
 *
 * TODO(AF-18): no frame draws the expanded chart. The antd shell is the stand-in; only the chart
 * inside it is specced.
 */
const OrbitChartExpandModal = NiceModal.create(({ model }: IOrbitChartExpandModalProps) => {
  const modal = useModal();
  const { t } = useMultiLanguageTranslation();
  const captureRef = useRef<HTMLDivElement>(null);
  const variant = useOrbitThemeVariant();
  const [downloading, setDownloading] = useState(false);

  /*
    The literal, not `orbitTokens.surface`: that is a `var()` reference, and the canvas the PNG is
    rasterised onto is painted by html-to-image outside any DOM that could resolve it.
  */
  const handleDownload = useCallback(() => {
    const node = captureRef.current;

    if (node === null) {
      return;
    }

    setDownloading(true);
    downloadOrbitChartImage(node, model.title, orbitColorsFor(variant).surface).then(
      () => setDownloading(false),
      () => setDownloading(false)
    );
  }, [model, variant]);

  return (
    <Modal
      {...antdModalV5(modal)}
      title={t(`${TOrbit}.analysis.expand`)}
      footer={null}
      width={MODAL_WIDTH}
      bodyStyle={{ padding: 12 }}
      data-testid="orbit-chart-expanded"
    >
      {/* antd portals the modal to `document.body`, clear of the shell's scope, so it declares
          the Orbit tokens again for its own subtree. */}
      <OrbitThemeScope>
        <Frame>
          <Surface ref={captureRef} data-testid="orbit-chart-expanded-capture">
            <Caption data-testid="orbit-chart-expanded-title">{model.title}</Caption>
            {/* `interactive={false}`: the expanded copy must not offer its own expand control. */}
            <OrbitChart model={model} interactive={false} />
          </Surface>
          <OrbitChartActions onDownload={handleDownload} downloading={downloading} />
        </Frame>
      </OrbitThemeScope>
    </Modal>
  );
});

export default OrbitChartExpandModal;
